import { ActionableNotification, InlineNotification } from '@carbon/react';

import {
  hasOwnershipChanges,
  ownershipChangesText,
  preservedExclusionsText,
} from '../ownership-changes-utils';
import type { CloneNotices } from '../types';
import styles from './select-assets-slots-step.module.scss';

export interface CloneNoticeBannersProps {
  /** The draft read's notices; null before it answers and on any draft but a ready clone. */
  notices: CloneNotices | null;
  /** Why the notices could not be read. */
  error: string | null;
  /** The source packages' names, in tag order. */
  sourceNames: string[];
  /** Whether the exclusions notice shows: the header's Hide/Show Notifications. */
  showExclusions: boolean;
  /** The exclusions notice was closed. */
  onExclusionsClose: () => void;
  /** Whether the ownership warning shows: the header's Hide/Show Notifications. */
  showOwnership: boolean;
  /** The ownership warning was closed. */
  onOwnershipClose: () => void;
  /** "Review asset changes". */
  onReview: () => void;
}

/**
 * A clone's notices above the tables on page 2: "Original exclusions preserved"
 * (what the sources leave unticked, shown even at none) and "Asset ownership
 * changes" (what the date removed and added, with the review). Each is hidden
 * by its own close or, with the other, by the header's Hide Notifications.
 *
 * @param {CloneNoticeBannersProps} props - The notices and their actions
 * @return {JSX.Element} The notices
 */
export function CloneNoticeBanners({
  notices,
  error,
  sourceNames,
  showExclusions,
  onExclusionsClose,
  showOwnership,
  onOwnershipClose,
  onReview,
}: CloneNoticeBannersProps) {
  const exclusions = notices?.preservedExclusions ?? null;
  const changes = notices?.ownershipChanges ?? null;

  return (
    <>
      {error !== null && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          className={styles.cloneNotice}
          title="Couldn't read the clone's notices"
          subtitle={error}
        />
      )}
      {exclusions !== null && showExclusions && (
        <InlineNotification
          kind="info"
          lowContrast
          className={styles.cloneNotice}
          title="Original exclusions preserved —"
          subtitle={preservedExclusionsText(exclusions, sourceNames)}
          onCloseButtonClick={onExclusionsClose}
        />
      )}
      {/*
        role="alert", as the step's other notices have it: an alertdialog takes focus on every
        render. Escape stays with the review modal it opens.
      */}
      {hasOwnershipChanges(changes) && showOwnership && (
        <ActionableNotification
          inline
          role="alert"
          closeOnEscape={false}
          kind="warning"
          lowContrast
          className={styles.cloneNotice}
          title="Asset ownership changes"
          subtitle={ownershipChangesText(changes)}
          actionButtonLabel="Review asset changes"
          onActionButtonClick={onReview}
          onCloseButtonClick={onOwnershipClose}
        />
      )}
    </>
  );
}
